import type {
  MobileFrontEndTimelineView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ARTIFACT_ID_FIELD } from './constants';
import { isGraphQLName } from './gql-literal';
import { artifactDisplayName, artifactTableView, artifactTimelineView } from './labels';
import { isSelectableFieldPath, resolvePath } from './message-mapping';
import { buildPagedQuery } from './selection-set';
import { timestampMillis } from './timestamps';
import { orderByIfSortable } from './where';

/** One positioned entry on the timeline. */
export interface TimelineEvent {
  id: string;
  /** Artifact type the entry came from; drives the icon and the deep link. */
  graphqlType: string;
  typeLabel: string;
  timestamp: string;
  /** Epoch millis, precomputed for sorting and grouping. */
  millis: number;
  title: string;
  summary: string | null;
  lane: string | null;
  icon: string | null;
  row: Record<string, unknown>;
}

/** Every field one timeline page must select. */
export function timelineFields(
  view: MobileFrontEndTimelineView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const fields = new Set<string>([ARTIFACT_ID_FIELD, view.timestamp_field]);
  for (const field of [
    view.title_field,
    view.summary_field,
    view.lane_field,
    ...(view.detail_fields ?? []),
  ]) {
    if (field) fields.add(field);
  }
  return [...fields].filter((field) => isSelectableFieldPath(artifact, field));
}

/** One page of timeline entries for a single artifact type. */
export function buildTimelineQuery(args: {
  rootQuery: string;
  view: MobileFrontEndTimelineView;
  artifact: MobileUiConfigArtifact | undefined;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  direction: 'asc' | 'desc';
  where?: Record<string, unknown> | null;
}): string | null {
  return buildPagedQuery({
    operationName: 'ArtifactTimeline',
    rootQuery: args.rootQuery,
    mobileAcquisitionId: args.mobileAcquisitionId,
    offset: args.offset,
    limit: args.limit,
    fields: timelineFields(args.view, args.artifact),
    where: args.where,
    orderBy: orderByIfSortable(
      args.view.timestamp_field,
      args.direction,
      artifactTableView(args.artifact),
    ),
  });
}

/** Map one artifact's rows into timeline entries, dropping undated rows. */
export function mapTimelineEvents(
  rows: Record<string, unknown>[],
  args: {
    view: MobileFrontEndTimelineView;
    graphqlType: string;
    typeLabel: string;
  },
): TimelineEvent[] {
  const { view, graphqlType, typeLabel } = args;
  const events: TimelineEvent[] = [];

  for (const [index, row] of rows.entries()) {
    const raw = resolvePath(row, view.timestamp_field);
    if (raw == null || raw === '') continue;
    const timestamp = String(raw);
    const millis = timestampMillis(timestamp, Number.NaN);
    // An unparseable date can't be positioned, and placing it at epoch zero
    // would drag the whole axis back to 1970.
    if (!Number.isFinite(millis)) continue;

    events.push({
      id: `${graphqlType}:${resolvePath(row, ARTIFACT_ID_FIELD) ?? index}`,
      graphqlType,
      typeLabel,
      timestamp,
      millis,
      title: asText(view.title_field ? resolvePath(row, view.title_field) : null) ?? typeLabel,
      summary: asText(view.summary_field ? resolvePath(row, view.summary_field) : null),
      lane: asText(view.lane_field ? resolvePath(row, view.lane_field) : null),
      icon: view.icon ?? null,
      row,
    });
  }

  return events;
}

/** Merge per-type event pages into one ordered stream. */
export function mergeTimelineEvents(
  pages: TimelineEvent[][],
  direction: 'asc' | 'desc',
  limit: number,
): TimelineEvent[] {
  const merged = pages.flat();
  merged.sort((a, b) => (direction === 'asc' ? a.millis - b.millis : b.millis - a.millis));
  return merged.slice(0, limit);
}

/** One artifact type participating in a cross-artifact timeline. */
export interface TimelineSource {
  graphqlType: string;
  typeLabel: string;
  rootQuery: string;
  view: MobileFrontEndTimelineView;
  artifact: MobileUiConfigArtifact;
}

/**
 * Artifact types that can appear on a merged timeline.
 *
 * Restricted to types with an authored `timeline` view, because a merged stream
 * needs a title and a timestamp the schema has vouched for — guessing which of
 * an artifact's several datetime fields means "when it happened" produces a
 * timeline that is confidently wrong.
 */
export function timelineSources(
  uiConfig: MobileUiConfigResponse | undefined,
  options: { families?: readonly string[]; limit?: number } = {},
): TimelineSource[] {
  if (!uiConfig) return [];
  const families = options.families ? new Set(options.families) : null;

  const sources: TimelineSource[] = [];
  for (const [graphqlType, artifact] of Object.entries(uiConfig.artifacts)) {
    const view = artifactTimelineView(artifact);
    const rootQuery = artifact.front_end?.graphql_root_query;
    if (!view || !rootQuery || !isGraphQLName(rootQuery)) continue;
    const family = artifact.front_end?.meta?.family;
    if (families && (!family || !families.has(family))) continue;

    sources.push({
      graphqlType,
      typeLabel: artifactDisplayName(artifact, graphqlType),
      rootQuery,
      view,
      artifact,
    });
  }

  sources.sort(
    (a, b) =>
      (b.artifact.front_end?.meta?.priority ?? 0) -
        (a.artifact.front_end?.meta?.priority ?? 0) ||
      a.typeLabel.localeCompare(b.typeLabel),
  );

  return options.limit ? sources.slice(0, options.limit) : sources;
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}
